import { Box, Skeleton, Stack } from "@mui/material";
import { useLicenseQuery } from "Api/Queries/Licenses/useLicenseQuery";
import {
  LICENSE_CODE_TO_DATA_MAP,
  getLicenseImage,
  getNextAvailableLicense,
} from "Components/Licenses/licenseUtils";
import { CarouselPicker } from "Components/Shared/Inputs/CarouselPicker/CarouselPicker";
import type {
  LicenseCode,
  LicenseTypeListItem,
  Region,
} from "Infrastructure/Api/Api";
import { Description } from "./Components/Description";
import { Perks } from "./Components/Perks";
import { Price } from "./Components/Price";
import { Resources, useResource } from "Translations/Resources";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const COMPONENT_RESOURCES = Resources.Components.LicensePicker;

type LicensePickerProps = {
  onSelect: (license: LicenseCode) => void;
  // Keeps an already-ordered license visible even when its tier sold out since;
  // a form without a preselected license has none.
  defaultValue?: LicenseCode;
  currentValue: LicenseCode;
  region: Region;
  isDisabled?: boolean;
  hasEdgeBleed?: boolean;
};

export const LicensePicker = ({
  onSelect,
  defaultValue,
  currentValue,
  region,
  isDisabled,
  hasEdgeBleed,
}: LicensePickerProps) => {
  const { t } = useResource();
  const {
    data: { items: licenses } = {},
    isLoading,
    isError,
  } = useLicenseQuery(region);

  if (isLoading) {
    return <LicensePickerSkeleton />;
  }

  if (!licenses || isError) {
    return <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>;
  }

  const licensesMap = licenses.reduce(
    (acc, license) => {
      acc[license.code] = license;
      return acc;
    },
    {} as Record<LicenseCode, LicenseTypeListItem>,
  );

  const filteredLicenseOptions = licenses
    .filter(
      ({ availableAmount, code }) =>
        availableAmount > 0 || code === defaultValue, // if the license tier isn't available anymore, but the user ordered it already (when it still was), include it for them
    )
    .sort((a, b) => a.price - b.price)
    .map(({ code }) => ({
      title: LICENSE_CODE_TO_DATA_MAP[code].title,
      value: code,
      imgSrc: getLicenseImage(code, region),
    }));

  // currentValue may briefly hold a license code from a previously selected
  // region (until the region-change handler resyncs the form). Fall back to a
  // valid license of this region so the carousel, description and price always
  // describe the same existing license.
  const selectedLicense =
    licensesMap[currentValue] ?? getNextAvailableLicense(licenses);

  if (!selectedLicense) {
    return <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>;
  }

  return (
    <>
      <CarouselPicker
        isDisabled={isDisabled}
        options={filteredLicenseOptions}
        onSelect={({ value }) => onSelect(value)}
        value={selectedLicense.code}
        hasEdgeBleed={hasEdgeBleed}
      />
      <Stack alignItems="center" textAlign="center" gap={3}>
        <Stack gap={1}>
          <Description licenseType={selectedLicense.code} />
          <Perks licenseType={selectedLicense.code} />
        </Stack>
        {/* <AvailabilityInfo
          current={selectedLicense.availableAmount}
          max={selectedLicense.totalAmount}
        /> */}
        <Price
          amount={selectedLicense.price}
          currencyCode={selectedLicense.currencyCode}
        />
      </Stack>
    </>
  );
};

export const LicensePickerSkeleton = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    gap={3}
    pt="0.75rem"
  >
    <Skeleton
      variant="rounded"
      width={210}
      height={366}
      sx={{ borderRadius: 4 }}
    />
    <Box display="flex" gap={1} justifyContent="center">
      <Skeleton variant="circular" width={12} height={12} />
      <Skeleton variant="circular" width={12} height={12} />
      <Skeleton variant="circular" width={12} height={12} />
      <Skeleton variant="circular" width={12} height={12} />
    </Box>
  </Box>
);
